import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';

interface NotificationsContentProps {
  segment: string;
  setSegment: (value: string) => void;
  params: any;
  setParams: (value: any) => void;
  title: string;
  setTitle: (value: string) => void;
  body: string;
  setBody: (value: string) => void;
  isLoading: boolean;
  onSubmit: () => void;
}

const NotificationsContent: React.FC<NotificationsContentProps> = ({
  segment,
  setSegment,
  params,
  setParams,
  title,
  setTitle,
  body,
  setBody,
  isLoading,
  onSubmit
}) => {
  const { theme } = useTheme();

  const handleParamChange = (key: string, value: string) => {
    setParams((prevParams: any) => ({
      ...prevParams,
      [key]: value
    }));
  };

  // Define os segmentos disponíveis
  const availableSegments = [
    { value: 'allUsers', label: 'Todos os Usuários' },
    { value: 'wishlistGame', label: 'Jogo na Wishlist (por ID)' },
    { value: 'ownedGames', label: 'Proprietários de Jogos' },
    { value: 'newUsers', label: 'Usuários Novos (últimos dias)' },
    { value: 'inactiveUsers', label: 'Usuários Inativos (sem atividade)' },
    { value: 'frequentUsers', label: 'Usuários Frequentes' },
    { value: 'postInteractions', label: 'Usuários que Interagiram com Posts' },
    { value: 'affiliateClicks', label: 'Usuários com Cliques em Afiliados' },
    { value: 'activeListings', label: 'Usuários com Listings Ativos para Compra' },
    { value: 'buyListingGame', label: 'Usuários com Anúncios de Compra de Jogo Específico' },
    // Adicionar outros segmentos conforme necessário
  ];

  // Tipagem explícita para eventos onChange
  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => setTitle(e.target.value);
  const handleBodyChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => setBody(e.target.value);
  const handleSegmentChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSegment(e.target.value);
    // Resetar params ao trocar de segmento
    setParams({});
  };
  const handleGameIdChange = (e: React.ChangeEvent<HTMLInputElement>) => handleParamChange('gameId', e.target.value);

  // Definir classes base para reutilização
  const baseInputClasses = `mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none sm:text-sm ${
    theme === 'dark' 
      ? 'bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-400 focus:ring-indigo-500 focus:border-indigo-500' 
      : 'border-gray-300 focus:ring-indigo-500 focus:border-indigo-500'
  }`;
  const baseSelectClasses = `mt-1 block w-full pl-3 pr-10 py-2 text-base border rounded-md shadow-sm focus:outline-none sm:text-sm ${
    theme === 'dark' 
      ? 'bg-gray-700 border-gray-600 text-gray-200 focus:ring-indigo-500 focus:border-indigo-500' 
      : 'border-gray-300 focus:ring-indigo-500 focus:border-indigo-500'
  }`;
  const labelClasses = `block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`;
  const cardClasses = `shadow-md rounded-lg p-6 border ${
    theme === 'dark' 
      ? 'bg-gray-800 border-gray-700' 
      : 'bg-white border-gray-200'
  }`;
  const headingClasses = `text-xl font-semibold mb-4 ${theme === 'dark' ? 'text-gray-100' : 'text-gray-800'}`;
  const mutedTextClasses = `text-xs font-normal mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`;

  return (
    <div className={cardClasses}>
      <h2 className={headingClasses}>Enviar Notificação Segmentada</h2>
      
      <div className="space-y-4">
        <div>
          <label htmlFor="segment-select" className={labelClasses}>Segmento de Usuários</label>
          <select 
            id="segment-select" 
            value={segment} 
            onChange={handleSegmentChange}
            disabled={isLoading}
            className={baseSelectClasses}
          >
            <option value="" disabled>Selecione o segmento...</option>
            {availableSegments.map((seg) => (
              <option key={seg.value} value={seg.value}>
                {seg.label}
              </option>
            ))}
          </select>
        </div>

        {/* Campos Condicionais Baseados no Segmento */}
        {segment === 'wishlistGame' && (
          <div>
            <label htmlFor="gameIdentifier" className={labelClasses}>ID ou Nome do Jogo</label>
            <input
              id="gameIdentifier"
              type="text"
              placeholder="Insira o UUID ou o Nome do jogo"
              value={params.gameName || params.gameId || ''}
              onChange={(e) => {
                  handleParamChange('gameName', e.target.value);
                  if (params.gameId) handleParamChange('gameId', ''); 
              }}
              disabled={isLoading}
              className={baseInputClasses}
            />
          </div>
        )}

        {segment === 'ownedGames' && (
          <div>
            <label htmlFor="gameIdentifier" className={labelClasses}>ID ou Nome do Jogo (opcional)</label>
            <input
              id="gameIdentifier"
              type="text"
              placeholder="Deixe em branco para todos os proprietários de jogos"
              value={params.gameName || params.gameId || ''}
              onChange={(e) => {
                handleParamChange('gameName', e.target.value);
                if (params.gameId) handleParamChange('gameId', ''); 
              }}
              disabled={isLoading}
              className={baseInputClasses}
            />
          </div>
        )}

        {segment === 'newUsers' && (
          <div>
            <label htmlFor="daysSinceRegistration" className={labelClasses}>Dias desde o cadastro</label>
            <input
              id="daysSinceRegistration"
              type="number"
              placeholder="Ex: 7 (para usuários dos últimos 7 dias)"
              value={params.daysSinceRegistration || ''}
              onChange={(e) => handleParamChange('daysSinceRegistration', e.target.value)}
              disabled={isLoading}
              min={1}
              className={baseInputClasses}
            />
          </div>
        )}

        {segment === 'inactiveUsers' && (
          <div>
            <label htmlFor="daysInactive" className={labelClasses}>Dias sem atividade</label>
            <input
              id="daysInactive"
              type="number"
              placeholder="Ex: 30 (para usuários inativos há 30 dias)"
              value={params.daysInactive || ''}
              onChange={(e) => handleParamChange('daysInactive', e.target.value)}
              disabled={isLoading}
              min={1}
              className={baseInputClasses}
            />
          </div>
        )}

        {segment === 'frequentUsers' && (
          <div>
            <label htmlFor="minInteractions" className={labelClasses}>Mínimo de interações nos últimos dias</label>
            <div className="grid grid-cols-2 gap-4">
              <input
                id="minInteractions"
                type="number"
                placeholder="Número de interações"
                value={params.minInteractions || ''}
                onChange={(e) => handleParamChange('minInteractions', e.target.value)}
                disabled={isLoading}
                min={1}
                className={baseInputClasses}
              />
              <input
                id="interactionDays"
                type="number"
                placeholder="Dias para considerar"
                value={params.interactionDays || ''}
                onChange={(e) => handleParamChange('interactionDays', e.target.value)}
                disabled={isLoading}
                min={1}
                className={baseInputClasses}
              />
            </div>
          </div>
        )}

        {segment === 'postInteractions' && (
          <div>
            <label htmlFor="interactionType" className={labelClasses}>Tipo de interação</label>
            <select
              id="interactionType"
              value={params.interactionType || ''}
              onChange={(e) => handleParamChange('interactionType', e.target.value)}
              disabled={isLoading}
              className={baseSelectClasses}
            >
              <option value="" disabled>Selecione o tipo...</option>
              <option value="likes">Curtidas</option>
              <option value="comments">Comentários</option>
              <option value="all">Qualquer interação</option>
            </select>
            
            <label htmlFor="daysSinceInteraction" className={`${labelClasses} mt-3`}>Dias desde a interação (opcional)</label>
            <input
              id="daysSinceInteraction"
              type="number"
              placeholder="Deixe em branco para qualquer período"
              value={params.daysSinceInteraction || ''}
              onChange={(e) => handleParamChange('daysSinceInteraction', e.target.value)}
              disabled={isLoading}
              min={1}
              className={baseInputClasses}
            />
          </div>
        )}

        {segment === 'affiliateClicks' && (
          <div>
            <label htmlFor="clicksConversion" className={labelClasses}>Status de conversão</label>
            <select
              id="clicksConversion"
              value={params.clicksConversion || ''}
              onChange={(e) => handleParamChange('clicksConversion', e.target.value)}
              disabled={isLoading}
              className={baseSelectClasses}
            >
              <option value="" disabled>Selecione o status...</option>
              <option value="all">Todos os cliques</option>
              <option value="converted">Apenas convertidos</option>
              <option value="not_converted">Não convertidos</option>
            </select>
            
            <label htmlFor="daysSinceClick" className={`${labelClasses} mt-3`}>Dias desde o clique (opcional)</label>
            <input
              id="daysSinceClick"
              type="number"
              placeholder="Deixe em branco para qualquer período"
              value={params.daysSinceClick || ''}
              onChange={(e) => handleParamChange('daysSinceClick', e.target.value)}
              disabled={isLoading}
              min={1}
              className={baseInputClasses}
            />
          </div>
        )}

        {segment === 'activeListings' && (
          <div>
            <label htmlFor="listingType" className={labelClasses}>Tipo de listing</label>
            <select
              id="listingType"
              value={params.listingType || ''}
              onChange={(e) => handleParamChange('listingType', e.target.value)}
              disabled={isLoading}
              className={baseSelectClasses}
            >
              <option value="" disabled>Selecione o tipo...</option>
              <option value="all">Todos os tipos</option>
              <option value="sell">Venda</option>
              <option value="buy">Compra</option>
              <option value="trade">Troca</option>
            </select>
            
            <label htmlFor="minDaysActive" className={`${labelClasses} mt-3`}>Mínimo de dias ativos (opcional)</label>
            <input
              id="minDaysActive"
              type="number"
              placeholder="Deixe em branco para qualquer duração"
              value={params.minDaysActive || ''}
              onChange={(e) => handleParamChange('minDaysActive', e.target.value)}
              disabled={isLoading}
              min={1}
              className={baseInputClasses}
            />
          </div>
        )}

        {segment === 'buyListingGame' && (
          <div>
            <label htmlFor="gameIdentifier" className={labelClasses}>ID ou Nome do Jogo</label>
            <input
              id="gameIdentifier"
              type="text"
              placeholder="Insira o UUID ou o Nome do jogo"
              value={params.gameName || ''}
              onChange={(e) => handleParamChange('gameName', e.target.value)}
              disabled={isLoading}
              className={baseInputClasses}
            />
            
            <label htmlFor="minDaysActive" className={`${labelClasses} mt-3`}>Mínimo de dias ativos (opcional)</label>
            <input
              id="minDaysActive"
              type="number"
              placeholder="Deixe em branco para qualquer duração"
              value={params.minDaysActive || ''}
              onChange={(e) => handleParamChange('minDaysActive', e.target.value)}
              disabled={isLoading}
              min={1}
              className={baseInputClasses}
            />
          </div>
        )}

        <div>
          <label htmlFor="notification-title" className={labelClasses}>Título da Notificação</label>
          <input
            id="notification-title"
            type="text"
            placeholder="Ex: Promoção Imperdível!"
            value={title}
            onChange={handleTitleChange}
            disabled={isLoading}
            className={baseInputClasses}
          />
        </div>

        <div>
          <label htmlFor="notification-body" className={labelClasses}>Corpo da Notificação</label>
          <textarea
            id="notification-body"
            placeholder="Ex: O jogo que você deseja está com 20% de desconto!"
            value={body}
            onChange={handleBodyChange}
            disabled={isLoading}
            rows={4}
            className={baseInputClasses}
          />
        </div>

        <div>
          <label htmlFor="custom-url" className={labelClasses}>
            URL de Redirecionamento (opcional)
            <span className={`ml-1 text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
              <svg xmlns="http://www.w3.org/2000/svg" className="inline h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </span>
            <span className={mutedTextClasses}>
              Se não informado, será usado um URL automático com base no segmento selecionado
            </span>
          </label>
          <input
            id="custom-url"
            type="text"
            placeholder="Ex: /games/cyberpunk-2077 ou /marketplace?filter=active"
            value={params.customUrl || ''}
            onChange={(e) => handleParamChange('customUrl', e.target.value)}
            disabled={isLoading}
            className={baseInputClasses}
          />
        </div>

        <button 
          type="button"
          onClick={onSubmit} 
          disabled={isLoading}
          className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white 
                      ${isLoading 
                        ? (theme === 'dark' ? 'bg-indigo-700 cursor-not-allowed' : 'bg-indigo-400 cursor-not-allowed') 
                        : (theme === 'dark' ? 'bg-indigo-500 hover:bg-indigo-600 focus:ring-indigo-600' : 'bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500')}
                      focus:outline-none focus:ring-2 focus:ring-offset-2 
                      ${theme === 'dark' ? 'dark:ring-offset-gray-800' : ''}`}
        >
          {isLoading ? (
            <>
              <svg className={`animate-spin -ml-1 mr-3 h-5 w-5 ${theme === 'dark' ? 'text-gray-200' : 'text-white'}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Enviando...
            </>
          ) : (
            'Enviar Notificação'
          )}
        </button>
      </div>
    </div>
  );
};

export default NotificationsContent; 